import queryClient from "client";
import {
  Box,
  DragDrop,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKEditor,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  GKTypography,
  Grid2,
  RadioGroup,
  Show,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import {
  SingleStockItemInterface,
  StockUniverseAlockItemInterface,
  StockUniverseInterface,
} from "interfaces/EquityBasket";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { handleCreateFormData } from "utils/formHelpers";
import { refetchQuery } from "utils/helpers";
import {
  recommendationStatusDrowdown,
  riskDropdown,
  timePerdioDropdown,
  volatilityDropdown,
} from "../equityBasketHelper";

const AddRecommendation = () => {
  const navigate = useNavigate();

  const { id, recommendationId } = useParams();

  const companies = useAppSelector((data) => data.app.companies);

  const [uploadError, setUploadError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      isinCode: "",
      allocation: "",
      targetPrice: "",
      publish: false,
      stopLoss: "",
      rationale: "",
      gridCode: "",
      companyName: "",
      recommendationType: "",
      status: "LIVE",
      volatility: "",
      riskProfile: [],
      assetId: null,
      timePeriod: "",
      researchFiles: [],
    },
  });

  /** ========================================================================================================= */
  const { mutate: handleStockSubmit, isLoading } = useMutation({
    mutationFn: (values: any) =>
      axiosInstance.post(
        `/model_portfolio/${id}/stock_recommendation/`,
        values,
      ),
    onSuccess: () => {
      queryClient?.refetchQueries(["STOKE_UNIVERSE"]);
      queryClient?.refetchQueries(["GET_MODEL_PORTFOLIO_DETAILS_DATA"]);
      queryClient?.refetchQueries(["GET_MODEL_PORTFOLIO_DETAILS_DATA"]);
    },
  });

  /** ========================================================================================================= */
  const { data: StocksList } = useFetchQuery<{ data: StockUniverseInterface }>({
    key: ["STOKE_UNIVERSE"],
    route: `model_portfolio/${id}/stock_universe/`,
  });

  /** ========================================================================================================= */
  const { data: recommendationDetails } = useFetchQuery<{
    data: SingleStockItemInterface;
  }>({
    key: ["GET_MODEL_PORTFOLIO_DETAILS_DATA", recommendationId],
    route: `/model_portfolio/${id}/stock_item/${recommendationId}/?is_recommendation=true`,
    enabled: Boolean(recommendationId),
    success: (response: { data: SingleStockItemInterface }) => {
      form.reset({
        ...response.data,
        companyName: response?.data?.companyName,
      });
    },
  });

  /** ========================================================================================================= */
  useFetchQuery({
    key: ["GET_DETAILS_DATA", form.getValues("assetId")],
    route: `/model_portfolio/${id}/stock_item/${form.getValues("assetId")}/`,
    enabled: Boolean(form.getValues("assetId")),
    success: (response: { data: SingleStockItemInterface }) => {
      form.setValue("isinCode", response?.data?.isinCode, {
        shouldDirty: true,
      });
      form.setValue("status", response?.data?.status, {
        shouldDirty: true,
      });
      form.setValue("isinCode", response?.data?.isinCode, {
        shouldDirty: true,
      });
      form.setValue("targetPrice", response?.data?.targetPrice, {
        shouldDirty: true,
      });
      form.setValue("stopLoss", response?.data?.stopLoss, {
        shouldDirty: true,
      });
      form.setValue("gridCode", response?.data?.gridCode, {
        shouldDirty: true,
      });
      form.setValue("companyName", response?.data?.companyName, {
        shouldDirty: true,
      });
      form.setValue("recommendationType", response?.data?.recommendationType, {
        shouldDirty: true,
      });
      form.setValue("volatility", response?.data?.volatility, {
        shouldDirty: true,
      });
      form.setValue("riskProfile", response?.data?.riskProfile, {
        shouldDirty: true,
      });
      form.setValue("timePeriod", response?.data?.timePeriod, {
        shouldDirty: true,
      });
    },
  });

  const { mutate: handleSubmit, isLoading: submitLoading } = useMutation({
    mutationKey: ["UPDATE_PORTFOLIO_DETAILS"],
    mutationFn: (data: any) =>
      axiosInstance.patch(
        `/model_portfolio/${id}/stock_item/${recommendationId}/?is_recommendation=true`,
        data,
      ),
    onSuccess: () => {
      navigate(-1);
      refetchQuery("GET_STOCK_DETAILS");
    },
  });

  const { mutate: handleDeleteRecommendation, isLoading: deleteLodaing } =
    useMutation({
      mutationKey: ["UPDATE_PORTFOLIO_DETAILS"],
      mutationFn: (recommendationId: any) =>
        axiosInstance.delete(
          `/model_portfolio/${id}/stock_item/${recommendationId}/?is_recommendation=true`,
        ),
      onSuccess: () => {
        navigate(-1);
        refetchQuery("GET_STOCK_DETAILS");
      },
    });

  const onSubmit = (values: any) => {
    if (recommendationId) {
      handleSubmit(
        handleCreateFormData({
          dirtyFields: form.formState.dirtyFields,
          values: values,
        }),
      );
    } else {
      handleStockSubmit(
        handleCreateFormData({
          dirtyFields: form.formState.dirtyFields,
          values: values,
        }),
      );
    }
  };

  form.watch();

  return (
    <DashboardLayout title="Add Recommendation">
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Grid2 container spacing={2}>
          <Box maxHeight={`calc(100vh - 170px)`} overflow={"auto"}>
            <Grid2 container size={6} spacing={2}>
              <Grid2 size={12}>
                <GKTypography variant="primaryDark">
                  Stock & Recommendation Details
                </GKTypography>
              </Grid2>
              <Grid2 size={12}>
                {recommendationId ? (
                  <Typography fontSize={18} fontWeight={600}>
                    {recommendationDetails?.data?.name}
                  </Typography>
                ) : (
                  <Controller
                    name="gridCode"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        disableClearable
                        inputLabel="Select Asset"
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Typography
                              color={"var(--white)"}
                              fontSize={8}
                              style={{
                                background:
                                  option?.instType === "EQ"
                                    ? "var(--secondary-900)"
                                    : "var(--orange-700)",
                                padding: "2px 4px",
                                borderRadius: 4,
                              }}
                            >
                              {option?.instType}
                            </Typography>
                            <Typography whiteSpace={"nowrap"}>
                              {option?.name}
                            </Typography>
                          </li>
                        )}
                        options={StocksList?.data?.allocItem || []}
                        value={
                          companies?.find(
                            (data: any) => data?.value === field?.value,
                          )?.name
                        }
                        onChange={(e, val: StockUniverseAlockItemInterface) => {
                          form.setValue("assetId", val?.id);

                          field.onChange(val?.gridCode);
                        }}
                        getOptionLabel={(option) => option.name || ""}
                        error={!!error}
                      />
                    )}
                  />
                )}
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="recommendationType"
                  control={form.control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel>Recommendation Type:</FormLabel>
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="BUY"
                          control={<GKRadio />}
                          label="Buy"
                        />
                        <FormControlLabel
                          value="HOLD"
                          control={<GKRadio />}
                          label="Hold"
                        />
                        <FormControlLabel
                          value="EXIT"
                          control={<GKRadio />}
                          label="Exit"
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <GKTypography variant="primaryDark">
                  Price & Market Inputs
                </GKTypography>
              </Grid2>
              <Grid2 size={6}>
                <Controller
                  name="targetPrice"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <GKTextField
                      {...field}
                      inputLabel="Target Price"
                      slotProps={{ input: { endAdornment: "₹" } }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={6}>
                <Controller
                  name="stopLoss"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <GKTextField
                      {...field}
                      inputLabel="Stop Loss"
                      slotProps={{ input: { endAdornment: "₹" } }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="status"
                  control={form.control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel>
                        <Typography variant="primaryLight">
                          change Rec. status to:
                        </Typography>
                      </FormLabel>
                      <RadioGroup {...field}>
                        {recommendationStatusDrowdown?.map((dataItem) => (
                          <FormControlLabel
                            value={dataItem.value}
                            control={<GKRadio />}
                            label={
                              <Typography
                                fontWeight={600}
                                color={dataItem.color}
                              >
                                {dataItem?.name}
                              </Typography>
                            }
                            key={dataItem?.name}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <GKTypography variant="primaryDark">
                  Investment Parameters
                </GKTypography>
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="timePeriod"
                  control={form.control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel>Time Horizon:</FormLabel>
                      <RadioGroup row {...field}>
                        {timePerdioDropdown?.map((dataItem) => (
                          <FormControlLabel
                            value={dataItem.value}
                            control={<GKRadio />}
                            label={dataItem?.name}
                            key={dataItem?.name}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="volatility"
                  control={form.control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel>Volatility:</FormLabel>
                      <RadioGroup row {...field}>
                        {volatilityDropdown?.map((dataItem) => (
                          <FormControlLabel
                            value={dataItem.value}
                            control={<GKRadio />}
                            label={dataItem?.name}
                            key={dataItem?.name}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="riskProfile"
                  control={form.control}
                  render={({ field }) => (
                    <GKSearchSelect
                      multiple
                      {...field}
                      disableClearable={false}
                      onChange={(e, val) => {
                        field.onChange(val);
                      }}
                      value={field.value}
                      options={riskDropdown}
                      inputLabel="Risk Profile"
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <GKTypography variant="primaryDark">
                  Rationale & Research
                </GKTypography>
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="rationale"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <GKEditor
                      {...field}
                      inputLabel="Rationale"
                      placeholder="Specify key screening factors like market cap, growth metrics, and sector focus, based on research-driven criteria."
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <GKTypography>Upload Research File</GKTypography>
                <Controller
                  name="researchFiles"
                  control={form.control}
                  render={({ field }) => (
                    <DragDrop
                      value={field.value}
                      onChange={field.onChange}
                      multiple
                      setUploadError={setUploadError}
                      uploadError={uploadError}
                      supportName={"PDF, DOCX or CSV"}
                      acceptType={".pdf,.docx,.csv"}
                    />
                  )}
                />
              </Grid2>
            </Grid2>
          </Box>
          <Grid2
            size={12}
            display={"flex"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            borderTop={`solid 1px var(--neutral-100)`}
            p={1}
            gap={1}
            pb={0}
          >
            <Show.When isTrue={Boolean(id) && Boolean(recommendationId)}>
              <GKButton
                variant="outlined"
                color="error"
                onClick={() => {
                  handleDeleteRecommendation(recommendationId);
                }}
                loading={isLoading || submitLoading || deleteLodaing}
              >
                Delete
              </GKButton>
            </Show.When>
            <GKButton
              variant="contained"
              type="submit"
              loading={isLoading || submitLoading || deleteLodaing}
              disabled={!form.formState.isDirty}
            >
              Save
            </GKButton>
          </Grid2>
        </Grid2>
      </form>
    </DashboardLayout>
  );
};

export default AddRecommendation;
